/**
 * Toast — the page-level notification; state comes from hooks/useToast.js.
 *
 * Replaces 46 inline copies (docs/DUPLICATION_AUDIT.md, B11). It is always a polite
 * live region (about half the copies had no role or aria-live, audit A20), and always
 * has a labelled dismiss button.
 *
 * Types: success, error, warning, info. Anything else renders as error, which is what
 * most copies did with an unknown type. A persistent info toast shows a spinner (the
 * Users page's in-flight "Registering…" message).
 *
 * @param {{type: string, message: string, persistent?: boolean}|null} toast
 * @param {function} onDismiss
 */

import './toast.css';

const TYPES = new Set(['success', 'error', 'warning', 'info']);

const ICON_PATHS = {
  success: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
  warning: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
  info: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  spinner: 'M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992m-4.992 0a8.25 8.25 0 0014.142 2.515M21.015 9.348a8.25 8.25 0 00-14.142-2.515',
};

const Toast = ({ toast, onDismiss }) => {
  if (!toast) return null;
  const type = TYPES.has(toast.type) ? toast.type : 'error';
  const spin = type === 'info' && toast.persistent;

  return (
    <div className={`ui-toast ui-toast--${type}`} role="status" aria-live="polite">
      <svg
        className={`ui-toast__icon${spin ? ' ui-toast__icon--spin' : ''}`}
        fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={ICON_PATHS[spin ? 'spinner' : type]} />
      </svg>
      <span>{toast.message}</span>
      <button type="button" onClick={onDismiss} className="ui-toast__close" aria-label="Dismiss">
        <svg className="ui-toast__close-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default Toast;
